// =====================================================================
// WeddingOS – /guest/drinks
// =====================================================================
// • Kein Doppel-Eintrag (Realtime dedupliziert)
// • Vergangene Bestellungen ganz unten
// • Wird dasselbe Getränk 2x bestellt → sticky Favorit oben
// =====================================================================

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchDrinkMenu, submitDrinkOrder, realtime, supabase } from "../../services/supabase";
import { loadGuestSession } from "../../lib/guestSession";
import InfoTip from "../../components/InfoTip";
import QtyStepper from "../../components/QtyStepper";
import "../../styles/theme.css";

const DRINKS_STEPS = [
  { title: "Getraenk waehlen", detail: "Scrolle durch die Karte und tippe auf das Getraenk, das du moechtest." },
  { title: "Anmerkung ergaenzen", detail: "Optional kannst du eine Anmerkung hinzufuegen, z. B. ohne Eis oder extra Zitrone." },
  { title: "Bestellen", detail: "Tippe auf Bestellen - deine Bestellung geht sofort an den Barkeeper." },
  { title: "Status beobachten", detail: "Sieh unten den aktuellen Status: Bestellt oder Fertig. Du bekommst ein Update sobald es fertig ist." },
  { title: "Favoriten", detail: "Bestellst du dasselbe Getraenk zweimal, erscheint es als Favorit ganz oben fuer schnelle Nachbestellungen." },
];

// Nur zwei Zustände: Bestellt / Fertig (siehe /bar).
const STATUS_LABELS = {
  ordered:   { label: "Bestellt",  color: "var(--ink-muted)", bg: "var(--surface)" },
  preparing: { label: "Bestellt",  color: "var(--ink-muted)", bg: "var(--surface)" },
  ready:     { label: "Fertig! ✦", color: "var(--forest)",    bg: "var(--forest-soft)" },
  delivered: { label: "Fertig! ✦", color: "var(--forest)",    bg: "var(--forest-soft)" },
};

export default function GuestDrinks() {
  const navigate = useNavigate();
  const session  = loadGuestSession();

  const [menu,       setMenu]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [selected,   setSelected]   = useState(null);
  const [note,       setNote]       = useState("");
  const [qty,        setQty]        = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState(null);
  const [done,       setDone]       = useState(false);
  const [myOrders,   setMyOrders]   = useState([]);
  const [drinksEnabled, setDrinksEnabled] = useState(true);

  // Feature-Check: leitet zurück wenn Hochzeit nicht Premium ODER Getränke deaktiviert
  useEffect(() => {
    if (!session?.wedding_id) return;
    supabase
      .rpc("get_wedding_features", { p_wedding_id: session.wedding_id })
      .then(({ data }) => {
        const f = Array.isArray(data) ? data[0] : data;
        if (f && (f.is_paid !== true || f.drinks_enabled === false)) {
          setDrinksEnabled(false);
          navigate("/guest", { replace: true });
        }
      })
      .catch(() => {});
  }, [session?.wedding_id]);

  // Menü laden
  useEffect(() => {
    if (!session?.wedding_id) return;
    fetchDrinkMenu(session.wedding_id)
      .then(setMenu)
      .catch(() => setError("Getränkekarte konnte nicht geladen werden."))
      .finally(() => setLoading(false));
  }, [session?.wedding_id]);

  // Realtime – Verfügbarkeit live (Bar schaltet Getränk aus/ein → sofort ausgrauen)
  useEffect(() => {
    if (!session?.wedding_id) return;
    return realtime.drinkMenu(session.wedding_id, () => {
      fetchDrinkMenu(session.wedding_id).then(setMenu).catch(() => {});
    });
  }, [session?.wedding_id]);

  // Eigene Bestellungen beim Start laden (für Persistenz + Favoriten)
  useEffect(() => {
    if (!session?.guest_id || !session?.wedding_id) return;
    supabase
      .from("drink_orders")
      .select("*")
      .eq("guest_id", session.guest_id)
      .eq("wedding_id", session.wedding_id)
      .order("created_at", { ascending: false })
      .then(({ data }) => {
        if (data?.length) setMyOrders(data);
      });
  }, [session?.guest_id]);

  // Realtime – dedupliziert + nur eigene Bestellungen tracken
  useEffect(() => {
    if (!session?.wedding_id) return;
    const unsub = realtime.drinkOrders(session.wedding_id, (payload) => {
      if (payload.eventType === "UPDATE") {
        setMyOrders((prev) =>
          prev.map((o) => o.id === payload.new.id ? { ...o, status: payload.new.status } : o)
        );
      }
      // INSERT vom Realtime ignorieren – wir fügen nach eigenem Submit manuell ein
    });
    return unsub;
  }, [session?.wedding_id]);

  const handleSubmit = useCallback(async () => {
    if (!selected) return;
    if (!session?.guest_id) {
      setError("Sitzung ungültig – bitte neu über den Tisch-Link einloggen.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const order = await submitDrinkOrder({
        guestId:   session.guest_id,
        drinkName: selected.name,
        note,
        quantity:  qty,
      });
      // Dedupliziert einfügen
      setMyOrders((prev) => {
        if (prev.some((o) => o.id === order.id)) return prev;
        return [order, ...prev];
      });
      setSelected(null);
      setNote("");
      setQty(1);
      setDone(true);
      setTimeout(() => setDone(false), 5000);
    } catch {
      setError("Bestellung konnte nicht aufgegeben werden. Bitte nochmal versuchen.");
    } finally {
      setSubmitting(false);
    }
  }, [selected, session, note, qty]);

  if (!session) return <NoSession />;

  // Favorit: Getränk das mind. 2x bestellt wurde
  const countByName = myOrders.reduce((acc, o) => {
    acc[o.drink_name] = (acc[o.drink_name] ?? 0) + 1;
    return acc;
  }, {});
  // Ausverkaufte Getränke (nicht als Favorit anbieten)
  const soldOutNames = new Set(menu.filter((i) => i.available === false).map((i) => i.name));
  const favorites = Object.entries(countByName)
    .filter(([, count]) => count >= 2)
    .map(([name]) => name)
    .filter((name) => !soldOutNames.has(name));

  const grouped    = groupBy(menu, "category");
  const categories = Object.keys(grouped).sort();

  // Bestellungen aufteilen: aktive (oben) und abgeschlossene (unten)
  const activeOrders    = myOrders.filter((o) => o.status !== "delivered");
  const deliveredOrders = myOrders.filter((o) => o.status === "delivered");

  return (
    <div className="wos-shell">
      <PageHeader onBack={() => navigate("/guest")} title="Getränke" />

      {/* ── Anleitung ── */}
      {!selected && !done && (
        <InfoTip
          short="🥂 Wähle dein Getränk – es wird direkt an deinen Tisch gebracht."
          steps={DRINKS_STEPS}
        />
      )}

      {done && <SuccessBanner message="Bestellung eingegangen! ✦" />}
      {error && <div className="wos-error">{error}</div>}

      {/* ── Sticky Favorit ── */}
      {favorites.length > 0 && !selected && (
        <div className="wos-rise" style={{ marginBottom: 16 }}>
          <p className="wos-eyebrow" style={{ marginBottom: 8 }}>⭐ Dein Favorit</p>
          {favorites.map((name) => (
            <button
              key={name}
              onClick={() => setSelected({ name, category: "" })}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                width: "100%", padding: "14px 18px", marginBottom: 8,
                background: "linear-gradient(135deg, var(--accent-soft), var(--surface))",
                border: "1.5px solid var(--accent)", borderRadius: "var(--radius-md)",
                cursor: "pointer", textAlign: "left",
              }}
            >
              <span style={{ fontFamily: "var(--font-display)", fontSize: "1.1rem", color: "var(--ink)" }}>
                {name}
              </span>
              <span style={{ fontSize: 13, color: "var(--accent)", fontWeight: 600 }}>Nochmal +</span>
            </button>
          ))}
          <div className="wos-divider" style={{ marginTop: 8 }}>
            <span className="wos-divider-icon" style={{ fontSize: 10 }}>KARTE</span>
          </div>
        </div>
      )}

      {/* ── Formular oder Karte ── */}
      {selected ? (
        <OrderForm
          drink={selected}
          note={note}
          onNoteChange={setNote}
          qty={qty}
          onQtyChange={setQty}
          onSubmit={handleSubmit}
          onCancel={() => { setSelected(null); setNote(""); setQty(1); setError(null); }}
          submitting={submitting}
          error={error}
        />
      ) : (
        <>
          {loading ? <LoadingPlaceholder /> : categories.length === 0 ? <EmptyMenu /> : (
            categories.map((cat) => (
              <CategorySection key={cat} category={cat} items={grouped[cat]} onSelect={setSelected} />
            ))
          )}
        </>
      )}

      {/* ── Aktive Bestellungen ── */}
      {activeOrders.length > 0 && !selected && (
        <MyOrders orders={activeOrders} title="AKTUELLE BESTELLUNGEN" />
      )}

      {/* ── Bestellhistorie (ganz unten) ── */}
      {deliveredOrders.length > 0 && !selected && (
        <MyOrders orders={deliveredOrders} title="BESTELLHISTORIE" dimmed />
      )}
    </div>
  );
}

// ── CategorySection ───────────────────────────────────────────────────

function CategorySection({ category, items, onSelect }) {
  // Eingeklappt starten – erst auf Klick die Getränke der Gruppe zeigen.
  const [open, setOpen] = useState(false);
  return (
    <div className="wos-rise" style={{ marginBottom: 24 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          display: "flex", alignItems: "center", gap: 10, marginBottom: 12,
          width: "100%", background: "none", border: "none", padding: 0,
          cursor: "pointer", textAlign: "left",
        }}
      >
        <span className="wos-eyebrow">{category}</span>
        <span style={{ fontSize: 11, color: "var(--ink-subtle)" }}>({items.length})</span>
        <div style={{ flex: 1, height: 1, background: "var(--line)" }} />
        <span style={{
          fontSize: 13, color: "var(--ink-muted)",
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s ease",
        }}>⌄</span>
      </button>
      {open && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {items.map((item) => <DrinkItem key={item.id} item={item} onSelect={onSelect} />)}
        </div>
      )}
    </div>
  );
}

function DrinkItem({ item, onSelect }) {
  const [pressed, setPressed] = useState(false);
  const soldOut = item.available === false;

  if (soldOut) {
    return (
      <div
        aria-disabled="true"
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          width: "100%", padding: "16px 18px",
          background: "var(--surface)", border: "1px solid var(--line)",
          borderRadius: "var(--radius-md)", opacity: 0.55, cursor: "not-allowed",
        }}
      >
        <span style={{ fontFamily: "var(--font-display)", fontSize: "1.1rem",
          color: "var(--ink-muted)", textDecoration: "line-through" }}>
          {item.name}
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em",
          textTransform: "uppercase", color: "var(--ink-subtle)" }}>
          Ausgetrunken
        </span>
      </div>
    );
  }

  return (
    <button
      onClick={() => onSelect(item)}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        width: "100%", padding: "16px 18px",
        background: pressed ? "var(--surface)" : "var(--bg-elevated)",
        border: "1px solid var(--line)", borderRadius: "var(--radius-md)",
        cursor: "pointer", transform: pressed ? "scale(0.99)" : "scale(1)",
        transition: "transform 0.1s ease, background 0.1s ease", textAlign: "left",
      }}
    >
      <span style={{ fontFamily: "var(--font-display)", fontSize: "1.1rem", color: "var(--ink)" }}>
        {item.name}
      </span>
      <span style={{ fontSize: 20, color: "var(--accent)", lineHeight: 1 }}>+</span>
    </button>
  );
}

// ── OrderForm ────────────────────────────────────────────────────────

function OrderForm({ drink, note, onNoteChange, qty, onQtyChange, onSubmit, onCancel, submitting }) {
  return (
    <div className="wos-rise">
      <div className="wos-card" style={{ marginBottom: 24 }}>
        <p className="wos-eyebrow" style={{ marginBottom: 8 }}>Deine Wahl</p>
        <p style={{ fontFamily: "var(--font-display)", fontSize: "1.5rem", margin: 0 }}>{drink.name}</p>
        {drink.category && <p className="wos-meta" style={{ marginTop: 4 }}>{drink.category}</p>}
      </div>
      <div className="wos-field">
        <label className="wos-label">Menge</label>
        <QtyStepper value={qty} onChange={onQtyChange} />
      </div>
      <div className="wos-field">
        <label className="wos-label" htmlFor="drink-note">
          Anmerkung <span style={{ color: "var(--ink-subtle)", textTransform: "none", letterSpacing: 0 }}>(optional)</span>
        </label>
        <textarea id="drink-note" className="wos-textarea" rows={2}
          placeholder="z.B. ohne Eis, extra Zitrone …"
          value={note} onChange={(e) => onNoteChange(e.target.value)}
          maxLength={120} style={{ resize: "none" }} />
      </div>
      <button className="wos-btn wos-btn-primary" onClick={onSubmit} disabled={submitting} style={{ marginBottom: 12 }}>
        {submitting ? <><span className="wos-spinner" />Wird bestellt …</> : "Bestellen ✦"}
      </button>
      <button className="wos-btn wos-btn-ghost" onClick={onCancel} disabled={submitting}>
        Anderes Getränk
      </button>
    </div>
  );
}

// ── MyOrders ────────────────────────────────────────────────────────

function MyOrders({ orders, title, dimmed }) {
  return (
    <div className="wos-rise" style={{ marginTop: 28, opacity: dimmed ? 0.65 : 1 }}>
      <div className="wos-divider" style={{ marginBottom: 16 }}>
        <span style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--ink-subtle)", whiteSpace: "nowrap" }}>
          {title}
        </span>
      </div>
      {orders.map((o) => {
        const cfg    = STATUS_LABELS[o.status] ?? STATUS_LABELS.ordered;
        const isReady = o.status === "ready";
        return (
          <div key={o.id} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "13px 16px",
            background: cfg.bg,
            border: `1px solid ${isReady ? "var(--forest)" : "var(--line)"}`,
            borderRadius: "var(--radius-md)", marginBottom: 8,
            transition: "background 0.4s ease",
          }}>
            <div>
              <span style={{ fontFamily: "var(--font-display)", fontSize: "1.05rem" }}>
                {o.quantity > 1 ? `${o.quantity}× ` : ""}{o.drink_name}
              </span>
              {o.note && (
                <span style={{ display: "block", fontSize: 11, color: "var(--ink-subtle)", marginTop: 2 }}>
                  {o.note}
                </span>
              )}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: cfg.color, letterSpacing: "0.05em" }}>
              {cfg.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function PageHeader({ onBack, title }) {
  return (
    <header className="wos-rise" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
      <button onClick={onBack} aria-label="Zurück" style={{
        background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)",
        width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 18, color: "var(--ink-muted)", cursor: "pointer", flexShrink: 0,
      }}>←</button>
      <div>
        <p className="wos-eyebrow">Gast-Aktion</p>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: "1.7rem", marginTop: 2 }}>{title}</h1>
      </div>
    </header>
  );
}

function SuccessBanner({ message }) {
  return (
    <div role="status" style={{ padding: "14px 18px", background: "var(--forest-soft)",
      border: "1px solid var(--forest)", borderRadius: "var(--radius-md)",
      marginBottom: 20, color: "var(--forest)", fontWeight: 500,
      fontSize: 15, animation: "wos-rise 0.3s ease", textAlign: "center" }}>
      {message}
    </div>
  );
}

function LoadingPlaceholder() {
  return (
    <div style={{ textAlign: "center", paddingTop: 48 }}>
      <div style={{ display: "flex", justifyContent: "center", gap: 4 }}>
        {[0,1,2].map((i) => (
          <div key={i} style={{ width: 6, height: 6, borderRadius: "50%",
            background: "var(--accent)", opacity: 0.5,
            animation: "wos-spin 1s ease-in-out infinite", animationDelay: `${i * 0.15}s` }} />
        ))}
      </div>
    </div>
  );
}

function EmptyMenu() {
  return (
    <div style={{ textAlign: "center", paddingTop: 48 }}>
      <p style={{ fontFamily: "var(--font-display)", fontSize: "1.4rem", color: "var(--ink-muted)" }}>
        Getränkekarte wird vorbereitet …
      </p>
    </div>
  );
}

function NoSession() {
  return (
    <div className="wos-shell" style={{ textAlign: "center", paddingTop: 80 }}>
      <p className="wos-meta">Sitzung abgelaufen. Bitte Einladungslink neu öffnen.</p>
    </div>
  );
}

function groupBy(arr, key) {
  return arr.reduce((acc, item) => {
    const k = item[key] ?? "Sonstiges";
    if (!acc[k]) acc[k] = [];
    acc[k].push(item);
    return acc;
  }, {});
}
